import type { TagFamily } from '@repo/types'
import { useQuery } from '@tanstack/react-query'
import { createApiCall } from 'services'
import { useMemo } from 'react'
import { TAG_SHOP_MOCK_ENABLED, mockTagProductsResponse } from 'mocks/tagShopMock'

export interface TagShopProductVariant {
  id: string;
  sku: string | null;
  title: string;
  quantity: number;
  price: number;
  unit_price: number;
  images: string[];
  // VRG-143 — the real Capability·Size·Quantity variant model. Additive/optional so
  // existing case-tier variants still type. capability drives tag_family + badges (VRG-141).
  capability?: string | null;
  size?: string | null;
  /**
   * The chip family, read from the authoritative Shopify variant metafield `custom.tag_family`
   * (VERA-790 — never inferred; a missing value BLOCKS the encode rather than guessing).
   *
   * VRG-252 — typed as the registry's `TagFamily` rather than `string`, which is what let the
   * encode helpers be called with a value they could not accept. NEVER shown to a buyer: the shop
   * surfaces capability, the chip is internal fulfilment detail.
   *
   * ⚠️ KNOWN, OUT OF SCOPE HERE: when the metafield is absent the API falls back to a
   * capability-derived DISPLAY name ("NTAG 213"), which is neither a valid family nor null — so
   * the operator is told "not supported on the desk reader yet" when the truth is "this variant is
   * missing its tag type in Shopify". Removing the fallback is the right fix and is gated on every
   * variant having its metafield set first (see the `tag-family-enforce-metafield` decision);
   * doing it before that halts live encoding.
   */
  tag_family?: TagFamily | null;
}

export interface TagShopProduct {
  id: string;
  sku: string;
  title: string;
  images: string[];
  best_for_product_types: string[];
  shopify_category_id?: string | null;
  category_type?: string | null;
  description: string | null;
  details: string | null;
  width: number | null;
  height: number | null;
  depth: number | null;
  /** Tag outline shape for the custom-logo canvas ('round' | 'rect'). */
  shape?: string | null;
  /** Printable sides (1 or 2) — a 2-sided tag takes a logo on each face. */
  sides?: number | null;
  unit_price: number;
  variants: TagShopProductVariant[];
  // VRG-143 — product = form factor; VRG-141 metafields. All optional/additive.
  /** form factor (Sticker/Solid/…) — the product axis. */
  form_factor?: string | null;
  /** distinct capabilities this form offers (Connect/Verify/… ) — drives faceted browse + badges. */
  capabilities?: string[] | null;
  /** VRG-152 — atomic leaf capabilities (e.g. ['Connect','Verify','Tamper']) for the
   * Shop's atomic Capability filter. `capabilities` (combined) still drives badges. */
  capabilityLeaves?: string[] | null;
  /** gates the On-Metal add-on (VRG-142). */
  on_metal_available?: boolean | null;
  /** available shapes (Round/Rectangle). */
  shapes?: string[] | null;
  /** discrete size options offered (e.g. ['30mm','38mm','45mm'] or ['S','M','L']) — drives the Size facet. */
  sizes?: string[] | null;
  /** VRG-156 configurator selections (product-page, not Shopify options). */
  /** Woven durability grades (Standard / Extra-Durable). */
  durability?: string[] | null;
  /** Woven embroidery selection (Plain / Embroidered). */
  embroidery?: string[] | null;
  /** Card construction (Plastic / Metal). */
  cardMaterial?: string[] | null;
}

export interface TagShopProductsResponse {
  products: TagShopProduct[];
  next_cursor?: string;
  /**
   * True when these are the DEV mock catalog rather than live Shopify products.
   * VERA-455 — any surface rendering this list must say so; a response the client
   * cannot question is one that lies convincingly.
   */
  mocked?: boolean;
}

type UseTagProductsParams = {
  limit?: number;
  /** @deprecated cursor-based pagination — kept for back-compat callers, ignored by the API */
  offset?: number;
  cursor?: string;
}

type UseTagProductsResult = {
  data: TagShopProductsResponse | null;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  refetch: () => void;
}

// Helper function to normalise API product to component format.
// The /shop/tags/products endpoint already returns the exact shape the UI
// expects (best_for_product_types[], and per-variant quantity + unit_price computed from the
// SKU server-side), so this is a defensive pass-through — trust server values.
function transformProduct(product: any): TagShopProduct {
  return {
    id: product.id,
    sku: product.sku,
    title: product.title,
    images: product.images || [],
    best_for_product_types: Array.isArray(product.best_for_product_types)
      ? product.best_for_product_types
      : product.best_for_product_types
        ? [product.best_for_product_types]
        : [],
    shopify_category_id: product.shopify_category_id ?? null,
    category_type: product.category_type ?? null,
    description: product.description ?? null,
    details: product.details ?? null,
    width: product.width ?? null,
    height: product.height ?? null,
    depth: product.depth ?? null,
    shape: product.shape ?? null,
    sides: product.sides ?? null,
    // VRG-141/143: capability model surfaced from the server (real Shopify path).
    capabilities: Array.isArray(product.capabilities) ? product.capabilities : null,
    capabilityLeaves: Array.isArray(product.capabilityLeaves) ? product.capabilityLeaves : null,
    form_factor: product.form_factor ?? null,
    on_metal_available: product.on_metal_available ?? null,
    shapes: Array.isArray(product.shapes) ? product.shapes : null,
    sizes: Array.isArray(product.sizes) ? product.sizes : null,
    durability: Array.isArray(product.durability) ? product.durability : null,
    embroidery: Array.isArray(product.embroidery) ? product.embroidery : null,
    cardMaterial: Array.isArray(product.cardMaterial) ? product.cardMaterial : null,
    unit_price: Number(product.unit_price) || 0,
    variants: (product.variants || []).map((variant: any) => ({
      id: variant.id,
      sku: variant.sku ?? null,
      title: variant.title,
      quantity: Number(variant.quantity) || 0,
      price: Number(variant.price) || 0,
      unit_price: Number(variant.unit_price) || 0,
      images: Array.isArray(variant.images)
        ? variant.images
        : variant.image
          ? [variant.image]
          : [],
      capability: variant.capability ?? null,
      size: variant.size ?? null,
      tag_family: variant.tag_family ?? null,
    })),
  };
}

export const useTagProducts = (
  params: UseTagProductsParams = {},
  options?: { enabled?: boolean }
): UseTagProductsResult => {
  // Create stable params to prevent infinite loops
  const stableParams = useMemo(
    () => ({
      limit: params.limit ?? 24,
      cursor: params.cursor,
    }),
    [params.limit, params.cursor]
  );

  // Only enable the query when we have valid parameters and it's not explicitly disabled
  const isEnabled = options?.enabled !== false && stableParams.limit > 0;

  const query = useQuery({
    queryKey: ['tag-shop-products', 'list', stableParams],
    queryFn: async () => {
      // DEV-only mock (VITE_TAG_SHOP_MOCK) — verify the storefront without live Shopify.
      if (TAG_SHOP_MOCK_ENABLED) {
        return mockTagProductsResponse
      }

      const response = await createApiCall({
        method: 'GET',
        url: '/shop/tags/products',
        params: {
          limit: stableParams.limit,
          ...(stableParams.cursor ? { cursor: stableParams.cursor } : {}),
        },
      });

      // Transform API response to match component expectations
      const transformedData: TagShopProductsResponse = {
        products: (response.data.products || []).map(transformProduct),
        next_cursor: response.data.next_cursor,
        // VERA-455: carried through so a surface can SAY it is showing the dev
        // mock catalog. Before this the API returned 200 with a normal-shaped
        // payload and no flag, so the platform rendered synthetic SKUs
        // (30-N-SOLID-100) as if they were the live store's. The operator's only
        // clue was a downstream "Missing tag type. Set it on this variant in
        // Shopify" for a SKU Shopify has never had.
        mocked: response.data.mocked === true,
      };

      return transformedData;
    },
    enabled: isEnabled,
    staleTime: 0, // Always fetch fresh data
    refetchOnMount: true,
    refetchOnWindowFocus: true,
  });

  return {
    data: query.data || null,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
};
